import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';
import PhilLifeLogo from '../../assets/images/PFI 120 Years Logo_PhilLife 120 Member.png';
import { ArrowUpAndDown, CreateProposalIcon, ProposalStatusIcon, ExtensionIcon, ProposalsIcon, ComplianceIcon, ComputationIcon, AmendmentIcon, NewAccountIcon, UserManagementIcon, ConfigurationIcon, AccountIcon, PolicyReviewIcon, ApprovalIcon, TeamApprovalIcon, TransferOwnershipIcon, RequestIcon, MyTeamIcon, GeneralChats } from '../../assets/icon/svg';
import { NavItem, navItems } from '../../data/system';
import { clearAllAuth, clearCredentials, selectUserDepartmentCode, selectUserEmail, selectUserRole } from '../../features/auth/auth-slice';
import { resetProposalForm } from '../../helper/proposal/proposal-reset-form';
import getFullName from '../../hooks/common/use-fullname';
import { getInitials } from '../../utils/get-initials.utils';
import { resetForm } from '../../features/it-user-forms/user-forms-slice';
import { useRefetchQuery } from '../../hooks/actions/use-refetch-query';
import { NAV_MENU_ID } from '../../constant/system';
import { useRequestAmendmentData } from '../../hooks/data-table/use-request-amendment-data.ts';
import { useExtensionProposal } from '../../hooks/proposal/use-extension-proposal';
import { setCurrentStep } from '../../features/gms-proposal-forms/steps-channel-proposal_as/steps-channel-proposal_as.slice';
import { UserInfo } from '../../api/user/user-api-state';
import ConfirmDefaultModal from '../global/modal/confirm-default-modal';
import { useLogOutMutation } from '../../api/auth/auth-api';
import { authSession } from '../../helper/auth/auth-session';
import { getErrorMessage } from '../../utils/get-error-message.utils';
import { useToastify } from '../../library/toastify/toast-provider';
import { useProposalAllComputationAccess } from '../../hooks/access/use-proposal-all-computation-access';
import { useProposalTypeData } from '../../hooks/proposal/use-proposal-type-data';
import { useConflictProposal } from '../../hooks/proposal/use-conflict-proposal';

export const getNavIcon = (id: number) => {
    switch (id) {
        case NAV_MENU_ID.CREATE_PROPOSAL:
            return <CreateProposalIcon />;
        case NAV_MENU_ID.PROPOSAL_STATUS:
            return <ProposalStatusIcon />;
        case NAV_MENU_ID.PROPOSAL_REQ_EXTENSION:
            return <ExtensionIcon />;
        case NAV_MENU_ID.BOOKED_PROPOSAL:
            return <ProposalsIcon />;
        case NAV_MENU_ID.COC_COMPLIANCE:
            return <ComplianceIcon />;
        case NAV_MENU_ID.POLICY_REVIEW:
            return <PolicyReviewIcon />;
        case NAV_MENU_ID.PROPOSAL_COMPUTATION:
            return <ComputationIcon />;
        case NAV_MENU_ID.APPROVAL_MY:
            return <ApprovalIcon />;
        case NAV_MENU_ID.APPROVAL_TEAM:
            return <TeamApprovalIcon />;
        case NAV_MENU_ID.REQUEST_AMENDMENT:
            return <AmendmentIcon />;
        case NAV_MENU_ID.NEW_ACCOUNT:
            return <NewAccountIcon />;
        case NAV_MENU_ID.USER_MANAGEMENT:
            return <UserManagementIcon />;
        case NAV_MENU_ID.CATEGORY_MANAGEMENT:
            return <ConfigurationIcon />;
        case NAV_MENU_ID.TRANSFER_OWN:
            return <TransferOwnershipIcon />;
        case NAV_MENU_ID.EXISTING_GROUP_REQ:
            return <RequestIcon />;
        case NAV_MENU_ID.ACCOUNT_SETTING:
            return <AccountIcon />;
        case NAV_MENU_ID.MY_TEAM:
            return <MyTeamIcon />;
        case NAV_MENU_ID.GENERAL_CHAT:
            return <GeneralChats />;
        default:
            return null;
    }
};

interface Props {
    collapsed: boolean;
    toggleMenu: (value: string) => void;
    openMenu: string | null;
    setOpenMenu: (label: string | null) => void;
    setMobileSidebar?: (value: boolean) => void;
    className?: string;
    mobileSidebar?: boolean;
    setCollapsed?: (value: boolean) => void;
    userData?: UserInfo;
}

export default function SidebarDashboard({ collapsed, toggleMenu, openMenu, setOpenMenu, className, setMobileSidebar, mobileSidebar, userData }: Props) {
    
    const { isITDepartment, isActuarialDepartment, isMarketingGMSDepartment, } = useProposalTypeData()
    const canFetchApprovalList = isActuarialDepartment || isMarketingGMSDepartment;
    const userDepartmentCode = useSelector(selectUserDepartmentCode);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { showToastNotify } = useToastify();
    const userRole = useSelector(selectUserRole);
    const email = useSelector(selectUserEmail);
    const [logOut, { isLoading: isLoadingLogOut }] = useLogOutMutation();
    const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);

    const { getRequestAmendments } = useRequestAmendmentData();
    const { getExtensionProposalRequest } = useExtensionProposal();
    const { getConflictProposalForApproval } = useConflictProposal();
    const { approvalTotal: approvalTotalTeam } = useProposalAllComputationAccess({mode: 'team', canFetch: canFetchApprovalList});
    const { approvalTotal: approvalTotalMy } = useProposalAllComputationAccess({mode: 'my', canFetch: canFetchApprovalList});

    const isChangePassword: boolean = userData?.mustChangePassword ?? false;

    const { 
        refetchCreateProposal, 
        refetchProposalStatus, 
        refetchAccountSetting,
        refetchUserManagement, 
        refetchNewAccount 
    } = useRefetchQuery();

    const firstAndLastLetter = getInitials(userData?.firstname, userData?.lastname);
    const userName = getFullName({
        firstName: userData?.firstname,
        lastName: userData?.lastname,
        middleName: userData?.middlename ?? '',
        suffix: userData?.suffix ?? '',
    });
    const userDepartment = userData?.department.name;

    const filteredNavItems = navItems.filter((item) => {
        if (!userDepartmentCode || !userRole) return false;
        const roleAllowed = !item.allowedRoles || item.allowedRoles.includes(userDepartmentCode);
        const positionAllowed = !item.allowedPositions || item.allowedPositions.includes(userRole);
        return roleAllowed && positionAllowed;
    });

    const isActive = (item: typeof navItems[0]) => {
        if (item.path && location.pathname === `/${item.path}`) return true;
        if (item.subMenu) return item.subMenu.some((sub) => location.pathname === `/${sub.path}`);
        return false;
    };

    const handleSelectMenu = (navItem: NavItem) => {
        if (navItem.id === NAV_MENU_ID.CREATE_PROPOSAL) refetchCreateProposal();
        if (navItem.id === NAV_MENU_ID.ACCOUNT_SETTING) refetchAccountSetting();
        if (navItem.id === NAV_MENU_ID.PROPOSAL_STATUS) refetchProposalStatus();
        if (navItem.id === NAV_MENU_ID.PROPOSAL_COMPUTATION) refetchProposalStatus();
        if (navItem.id === NAV_MENU_ID.USER_MANAGEMENT && isITDepartment) refetchUserManagement();
        if (navItem.id === NAV_MENU_ID.NEW_ACCOUNT) refetchNewAccount();

        setOpenMenu(null);
        setMobileSidebar?.(false);
        resetProposalForm(dispatch);
        dispatch(setCurrentStep(1));
        dispatch(resetForm());
    };

    const handleSelectSubMenu = (label: string) => {
        setOpenMenu(label);
        setMobileSidebar?.(false);
    };

    const handleLogout = async () => {
        if (!email) {
            showToastNotify('Email is required.', 'warning');
            return;
        }

        try {
            await logOut({ email }).unwrap();
            dispatch(clearCredentials());
            setShowLogoutModal(false);
            showToastNotify('Logout successfully.', 'success');
            dispatch(clearAllAuth());
            authSession.clearSession();
            navigate('/login');
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    };

    const approvalTeamBadgeText = approvalTotalTeam === 0 ? '' : approvalTotalTeam > 99 ? '99+' : approvalTotalTeam.toString();
    const approvalMyBadgeText = approvalTotalMy === 0 ? '' : approvalTotalMy > 99 ? '99+' : approvalTotalMy.toString();

    const amendmentCount = getRequestAmendments?.data?.length ?? 0;
    const amendmentBadgeText = amendmentCount === 0 ? '' : amendmentCount > 99 ? '99+' : amendmentCount.toString();

    const extensionCount = getExtensionProposalRequest?.data?.length ?? 0;
    const extensionBadgeText = extensionCount === 0 ? '' : extensionCount > 99 ? '99+' : extensionCount.toString();

    const existingGroupNameListCount = getConflictProposalForApproval?.data?.length ?? 0;
    const existingGroupNameListText = existingGroupNameListCount === 0 ? '' : existingGroupNameListCount > 99 ? '99+' : existingGroupNameListCount.toString();

    const notificationBadgeClass = (isItemActive: boolean) => `ml-2 flex min-w-4 items-center justify-center rounded-full px-1 py-0.5 text-[8px] font-bold leading-none ${isItemActive ? 'bg-[#186cb2] text-white' : 'bg-red-500 text-white'}`;

    const getNotificationBadge = (itemId: number) => {
        if (itemId === NAV_MENU_ID.REQUEST_AMENDMENT) return amendmentBadgeText;
        if (itemId === NAV_MENU_ID.PROPOSAL_REQ_EXTENSION) return extensionBadgeText;
        if (itemId === NAV_MENU_ID.APPROVAL_TEAM) return approvalTeamBadgeText;
        if (itemId === NAV_MENU_ID.APPROVAL_MY) return approvalMyBadgeText;
        if (itemId === NAV_MENU_ID.EXISTING_GROUP_REQ) return existingGroupNameListText;
        return '';
    };

    return (
        <aside className={`flex min-h-0 flex-shrink-0 flex-col justify-between overflow-hidden bg-white transition-all duration-300 ${mobileSidebar ? 'w-full' : collapsed ? 'w-[68px] border-r border-gray-200' : 'w-[220px] border-r border-gray-200'} ${className ?? ''}`}>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {!mobileSidebar && (
                    <div className={`flex flex-shrink-0 items-center justify-center border-b border-gray-100 py-4 ${collapsed ? 'px-2' : 'px-4'}`}>
                        <img src={PhilLifeLogo} alt="PhilLife Logo" className={`object-contain transition-all duration-300 ${collapsed ? 'h-8 w-10' : 'h-10 w-auto'}`} />
                    </div>
                )}

                <nav className="flex-1 overflow-y-auto px-2 py-2">
                    <div className="space-y-0.5">
                        {filteredNavItems.map((item) => {
                            const isItemActive = isActive(item);
                            const isItemOpen = openMenu === item.label;
                            const notificationBadge = getNotificationBadge(item.id);
                            const hasNotification = notificationBadge !== '' || (item.id === NAV_MENU_ID.ACCOUNT_SETTING && isChangePassword);

                            return (
                                <div key={item.label}>
                                    {item.subMenu ? (
                                        <button
                                            type="button"
                                            onClick={() => toggleMenu(item.label)}
                                            disabled={collapsed}
                                            title={collapsed ? item.label : undefined}
                                            className={`group relative flex w-full items-center rounded-lg px-2 py-1.5 text-left text-[11px] font-medium transition-all duration-200 ${collapsed ? 'justify-center' : 'justify-between'} ${isItemActive || isItemOpen ? 'bg-[#186cb2]/7 text-[#186cb2]' : 'text-gray-600 hover:bg-gray-50 hover:text-[#186cb2]'}`}
                                        >
                                            {(isItemActive || isItemOpen) && !collapsed && (
                                                <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-[#186cb2]" />
                                            )}
                                            <div className={`flex min-w-0 items-center ${collapsed ? 'justify-center' : 'gap-2.5'}`}>
                                                <span className={`relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-all duration-200 ${isItemActive || isItemOpen ? 'bg-[#186cb2] text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-[#186cb2]/10 group-hover:text-[#186cb2]'}`}>
                                                    {getNavIcon(item.id)}
                                                    {collapsed && hasNotification && (
                                                        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border border-white bg-red-500" />
                                                    )}
                                                </span>
                                                {!collapsed && (
                                                    <span className="min-w-0 truncate">
                                                        {item.label}
                                                    </span>
                                                )}
                                            </div>
                                            {!collapsed && item.subMenu?.length > 0 && (
                                                <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md ${isItemOpen ? 'text-[#186cb2]' : 'text-gray-400'}`}>
                                                    <ArrowUpAndDown isOpen={isItemOpen} />
                                                </span>
                                            )}
                                        </button>
                                    ) : (
                                        <Link
                                            to={`/${item.path || '#'}`}
                                            onClick={() => handleSelectMenu(item)}
                                            title={collapsed ? item.label : undefined}
                                            className={`group relative flex w-full items-center rounded-lg px-2 py-1.5 text-[11px] font-medium no-underline transition-all duration-200 
                                                ${collapsed ? 'justify-center' : 'justify-start gap-2.5'} 
                                                ${isItemActive ? 'bg-[#186cb2]/8 text-[#186cb2]' : 'text-gray-600 hover:bg-gray-50 hover:text-[#186cb2]'}
                                                ${isItemActive && !collapsed ? 'bg-[#186cb2]/10' : ''}
                                            `}
                                        >
                                            {isItemActive && !collapsed && (
                                                <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-[#186cb2]" />
                                            )}
                                            {/* <span className={`relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-all duration-200 ${isItemActive ? 'bg-[#186cb2] text-white shadow-sm' : 'bg-gray-100 text-gray-500 group-hover:bg-[#186cb2]/10 group-hover:text-[#186cb2]'}`}> */}
                                            <span
                                                className={`relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-all duration-200 ${
                                                    isItemActive
                                                        ? 'bg-[#186cb2] text-white shadow-sm'
                                                        : 'bg-gray-100 text-gray-500 group-hover:bg-[#186cb2]/10 group-hover:text-[#186cb2]'
                                                }`}
                                            >
                                                {getNavIcon(item.id)}
                                                {/* {collapsed && hasNotification && (
                                                    <span className={`absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border ${isItemActive ? 'border-[#186cb2]' : 'border-white'} ${item.id === NAV_MENU_ID.ACCOUNT_SETTING ? 'bg-yellow-400' : 'bg-red-500'}`} />
                                                )}
                                                {collapsed && hasNotification && (
                                                    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-red-500 px-0.5 text-[7px] font-bold leading-none text-white">
                                                        {notificationBadge || '!'}
                                                    </span>
                                                )} */}
                                                {notificationBadge && (
                                                    <span
                                                        className={`absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 px-0.5 text-[7px] font-bold leading-none 
                                                            ${isItemActive ? 'border-white bg-red-500 text-white'
                                                                : 'border-white bg-red-500 text-white'
                                                        }`}
                                                    >
                                                        {notificationBadge}
                                                    </span>
                                                )}

                                                {/* ACCOUNT SETTING WARNING */}
                                                {item.id ===
                                                    NAV_MENU_ID.ACCOUNT_SETTING &&
                                                    isChangePassword && (
                                                        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-yellow-400 text-[8px] font-bold leading-none text-gray-900">
                                                            !
                                                        </span>
                                                    )}
                                            </span>
                                            {!collapsed && (
                                                <div className="relative flex min-w-0 flex-1 items-center">
                                                    <span className="min-w-0 flex-1 truncate">
                                                        {item.label}
                                                    </span>
                                                    {/* {notificationBadge && (
                                                        <span className={notificationBadgeClass(isItemActive)}>
                                                            {notificationBadge}
                                                        </span>
                                                    )} */}
                                                    {/* {item.id === NAV_MENU_ID.ACCOUNT_SETTING && isChangePassword && (
                                                        <span className="ml-2 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-yellow-400 text-[8px] font-bold text-gray-900">
                                                            !
                                                        </span>
                                                    )} */}
                                                </div>
                                            )}
                                        </Link>
                                    )}
                                    {!collapsed && isItemOpen && item.subMenu && (
                                        <div className="ml-5 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2">
                                            {item.subMenu.map((sub) => {
                                                const isSubActive = location.pathname === `/${sub.path}`;
                                                return (
                                                    <Link
                                                        key={sub.label}
                                                        to={`/${sub.path}`}
                                                        onClick={() => handleSelectSubMenu(item.label)}
                                                        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[10px] font-medium no-underline transition-all duration-150 ${isSubActive ? 'bg-[#186cb2]/7 font-semibold text-[#186cb2]' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`}
                                                    >
                                                        <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${isSubActive ? 'bg-[#186cb2]' : 'bg-gray-300'}`} />
                                                        <span className="truncate">
                                                            {sub.label}
                                                        </span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </nav>
            </div>

            <div className="flex flex-shrink-0 flex-col border-t border-gray-100 bg-[#f8fafc]">
                <button
                    type="button"
                    onClick={() => setShowLogoutModal(true)}
                    title="Logout"
                    className={`group flex w-full items-center rounded-lg px-3 py-2.5 text-xs font-medium text-red-600 transition-all duration-200 hover:bg-red-50 active:scale-[0.98] ${collapsed ? 'justify-center' : 'gap-3'}`}
                >
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500 transition-colors duration-200 group-hover:bg-red-100 group-hover:text-red-600">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 17l5-5-5-5" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H3" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 5V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-1" />
                        </svg>
                    </span>

                    {!collapsed && (
                        <div className="min-w-0 flex-1 text-left">
                            <p className="truncate text-xs font-semibold text-red-600">
                                Logout
                            </p>
                            <p className="truncate text-[10px] font-normal text-gray-400">
                                Sign out of your account
                            </p>
                        </div>
                    )}
                </button>

                <div className={`flex items-center rounded-lg bg-white px-2.5 py-2.5 shadow-sm ring-1 ring-gray-100 ${collapsed ? 'mx-2 justify-center' : 'mx-2 gap-2.5'}`}>
                    <div className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#186cb2] text-[11px] font-bold uppercase text-white shadow-sm ring-2 ring-[#186cb2]/10">
                        {firstAndLastLetter}
                        <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500" />
                    </div>

                    {!collapsed && (
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold leading-tight text-gray-800">
                                {userName}
                            </p>
                            <p className="mt-1 truncate text-[10px] leading-tight text-gray-400">
                                {userDepartment}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {showLogoutModal && (
                <ConfirmDefaultModal
                    modalTitle="Confirm Logout"
                    modalSubTitle="You will be signed out of your account. Do you want to continue?"
                    onCancel={() => setShowLogoutModal(false)}
                    onConfirm={handleLogout}
                    isLoading={isLoadingLogOut}
                />
            )}
        </aside>
    );
}